import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homePrimaryContentItemContent$key } from "~/queries/__generated__/homePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  LinkHighlightArea,
  Section,
  StyledEntityLink,
  Summary,
} from "./styles";

export type Props = {
  reference: homePrimaryContentItemContent$key;
  variantProps?: ContentItemHomePrimaryVariantProps;
} & ContentItemRenderVariantProps;

export type ContentItemHomePrimaryVariantProps = {
  withLiveTag?: boolean;
};

export const ContentItemHomePrimary = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      onClick,
      provider,
      reference: reference_,
      variantProps = {
        withLiveTag: false,
      },
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment homePrimaryContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <Content>
          <Section>{provider.sections()}</Section>

          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <Headline>
                {provider.headline({ preferSocialHeadline: true })}
                {variantProps?.withLiveTag && provider.liveTag()}
              </Headline>
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {provider.actionBar({
              isBookmarkVisible: false,
              variant: "home",
            })}
          </ActionBar>
        </Content>
        <CoverImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size540x360",
                homeDesktopUp: "size540x360",
                mobileUp: "size320x240",
                tabletUp: "size540x360",
              },
              withCaption: true,
            })}
          </StyledEntityLink>
        </CoverImage>
      </Container>
    );
  },
);

ContentItemHomePrimary.displayName = "ContentItemHomePrimary";
